// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/hooks/useOrdemServico.ts
// ════════════════════════════════════════════════════════════════════════════

import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ordemServicoService, type OsListaFiltros } from '../services/ordemServicoService';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import type { CriarOsRequest, AtualizarOsRequest } from '../types';

interface UseOrdemServicoParams {
  pagina?: number;
  tamanhoPagina?: number;
  /** Busca, status, período, cliente, vendedor, técnico, status do cadastro, prioridade e ordem — no servidor. */
  filtros?: OsListaFiltros;
}

export const useOrdemServico = ({ pagina = 1, tamanhoPagina = 20, filtros = {} }: UseOrdemServicoParams = {}) => {
  const queryClient = useQueryClient();

  // A lista já traz `resumo` (KPIs do filtro sem o status): uma ida ao servidor.
  const { data: paginado, isLoading, isFetching } = useQuery({
    queryKey: ['os', pagina, tamanhoPagina, filtros],
    queryFn: () => ordemServicoService.getAll(pagina, tamanhoPagina, filtros),
    placeholderData: keepPreviousData, // a tabela não pisca a cada letra da busca
  });

  const ordens       = paginado?.itens       ?? [];
  const total        = paginado?.total        ?? 0;
  const totalPaginas = paginado?.totalPaginas ?? 1;
  const resumo       = paginado?.resumo;

  const createMutation = useMutation({
    mutationFn: (request: CriarOsRequest) => ordemServicoService.create(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['os'] });
      toast.success('Ordem de Serviço criada com sucesso');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AtualizarOsRequest }) =>
      ordemServicoService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['os'] });
      toast.success('Ordem de Serviço atualizada com sucesso');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => ordemServicoService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['os'] });
      toast.success('Ordem de Serviço excluída com sucesso');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  return {
    ordens,
    total,
    totalPaginas,
    resumo,
    isLoading,
    isFetching,
    createOs: createMutation.mutate,
    updateOs: updateMutation.mutate,
    deleteOs: deleteMutation.mutate,
    isCreating: createMutation.isPending,
  };
};
